/** FAQ, Support, and Advertising page copy (English source of truth). */
export const pagesMessagesEn = {
  faq: {
    pageTitle: "Frequently Asked Questions",
    pageSubtitle: "Find answers to common questions about WasOK",
    seoTitle: "FAQ — WasOK",
    seoDescription:
      "Answers to common questions about WasOK, scanning products, privacy, and ratings.",
    items: [
      {
        question: "How does WasOK work?",
        answer:
          "WasOK helps you remember which products you liked or disliked by scanning barcodes and saving your ratings and notes. Simply scan a product's barcode, rate it, add notes, and the next time you scan the same product, you'll instantly see your previous review.",
      },
      {
        question: "Is my data stored in the cloud?",
        answer:
          "No! All your data is stored locally on your device. We don't sync your information to the cloud, which means your shopping history and product opinions remain completely private. However, this also means you should back up your device if you want to preserve your data.",
      },
      {
        question: "Do I need an internet connection to use WasOK?",
        answer:
          "No, WasOK works completely offline. Since all your data is stored locally on your device, you can scan products, add ratings, and view your history without any internet connection.",
      },
      {
        question: "Can I use WasOK for any type of product?",
        answer:
          "Yes! WasOK works with any product that has a barcode. Whether it's food, cosmetics, household items, electronics, or anything else with a barcode, you can scan it and save your opinions.",
      },
      {
        question: "How do I scan a barcode?",
        answer:
          "Simply open the WasOK app and tap the scan button. Point your camera at the product's barcode, and the app will automatically detect and scan it. Make sure the barcode is well-lit and in focus for best results.",
      },
      {
        question: "Can I add photos to my product reviews?",
        answer:
          "Yes! You can attach photos to any product review. This is especially helpful for remembering packaging details, ingredient lists, or any other visual information that might help you remember why you liked or disliked a product.",
      },
      {
        question: "What information can I save about products?",
        answer:
          "You can save ratings (1-5 stars), text notes, photos, the price you paid, where you bought it, and custom categories. This comprehensive information helps you make informed decisions when shopping.",
      },
      {
        question: "Can I create shopping lists?",
        answer:
          "Yes! WasOK allows you to create shopping lists from products you've already reviewed. This makes it easy to remember which products you loved and want to buy again.",
      },
      {
        question: "How do I organize my products?",
        answer:
          "You can organize products using custom categories. For example, you might create categories like 'Snacks', 'Coffee', 'Skincare', or 'Cleaning Products'. This makes it easy to browse your reviews by type.",
      },
      {
        question: "Is WasOK free to use?",
        answer:
          "Yes, WasOK is completely free to download and use. There are no in-app purchases, subscriptions, or hidden fees. We believe everyone should have access to a private way to remember their product preferences.",
      },
      {
        question: "Is WasOK accessible?",
        answer:
          "WasOK is designed to be usable by as many people as possible. The app includes accessibility features such as screen reader support, scalable text, and an optional high-contrast mode.",
      },
      {
        question: "What happens if I uninstall the app?",
        answer:
          "Since all data is stored locally on your device, uninstalling the app will delete all your product reviews, notes, and shopping lists. Make sure to export your data if you want to keep a backup before uninstalling.",
      },
      {
        question: "Can I export my data?",
        answer:
          "Yes, WasOK allows you to export your data for backup purposes. You can export all your reviews to a file that you can save to your device or cloud storage of your choice.",
      },
      {
        question: "Does WasOK track my location?",
        answer:
          "No, WasOK does not track your location. While you can manually enter the store name where you bought a product, the app does not use GPS or location services.",
      },
      {
        question: "Can I use WasOK on multiple devices?",
        answer:
          "Currently, WasOK does not sync between devices since all data is stored locally. Each device will have its own separate database of products and reviews.",
      },
      {
        question: "What if a barcode doesn't scan?",
        answer:
          "If a barcode doesn't scan, make sure it's well-lit and in focus. You can also try cleaning the camera lens. If the barcode is damaged or unclear, you may be able to manually enter the barcode number.",
      },
    ],
    cta: {
      heading: "Still have questions?",
      body: "Reach out to our support team — we're happy to help.",
      button: "Contact support",
    },
  },
  support: {
    pageTitle: "Support",
    seoTitle: "Support — WasOK",
    seoDescription:
      "Get help with WasOK: contact support, report bugs, and share feedback.",
    getHelp: {
      title: "Get Help",
      body: "We're here to help you get the most out of WasOK. If you're experiencing issues or have questions, we have several resources available to assist you.",
    },
    faq: {
      title: "Frequently Asked Questions",
      body: "Before reaching out, check our FAQ section where we answer the most common questions about WasOK. You'll find detailed information about how the app works, privacy, data storage, and more.",
    },
    contact: {
      title: "Contact Support",
      body: "If you can't find the answer you're looking for, our support team is ready to help. You can reach us at:",
      emailLabel: "Email:",
      responseTime: "We typically respond within 24-48 hours",
    },
    bugs: {
      title: "Reporting Bugs",
      body: "Found a bug? We appreciate your help in making WasOK better. When reporting a bug, please include:",
      items: [
        "A detailed description of the issue",
        "Steps to reproduce the problem",
        "Your device model and Android version",
        "Screenshots or screen recordings if possible",
      ],
      sendPrefix: "Send bug reports to:",
    },
    featureRequests: {
      title: "Feature Requests",
      bodyBefore: "Have an idea for a new feature? We'd love to hear from you! Send your suggestions to",
      bodyAfter:
        ". While we can't implement every request, we carefully consider all feedback when planning future updates.",
    },
    knownIssues: {
      title: "Known Issues",
      body: "We're currently aware of the following issues and working on fixes:",
      items: [
        "Some barcodes may not scan in low light conditions — try using a flashlight",
        "Very long product notes may take a moment to save on older devices",
        "Image thumbnails may appear pixelated on high-resolution displays",
      ],
    },
    appUpdates: {
      title: "App Updates",
      body: "We regularly release updates with new features, improvements, and bug fixes. Make sure to keep WasOK updated through the Google Play Store to get the latest enhancements.",
    },
    community: {
      title: "Community",
      body: "Join our community of users who are making smarter shopping decisions with WasOK. Share tips, ask questions, and connect with other users on our social channels.",
    },
    emergency: {
      title: "Emergency Support",
      body: 'If you\'re experiencing a critical issue that prevents you from using the app, please mark your email as "URGENT" in the subject line, and we\'ll prioritize your request.',
    },
    cta: {
      heading: "Still need help?",
      body: "Our support team is here for you. Don't hesitate to reach out with any questions or concerns.",
      button: "Email Support",
    },
  },
  advertising: {
    pageTitle: "Advertising & Monetization",
    seoTitle: "Advertising — WasOK",
    seoDescription: "How WasOK approaches advertising and monetization.",
    approach: {
      title: "Our Approach",
      body: "At WasOK, we believe that your shopping habits and product opinions are private and personal. Our business model reflects this core value.",
    },
    noAds: {
      title: "No Advertisements",
      body: "WasOK does not display any advertisements. We will never show you ads based on the products you scan, the ratings you give, or any other data you create in the app. Your experience with WasOK is completely ad-free.",
    },
    noDataSelling: {
      title: "No Data Selling",
      body: "We do not sell, rent, or share your data with advertisers, data brokers, or any third parties. Since all your data is stored locally on your device, we don't even have access to it. Your product reviews, shopping lists, and notes are yours alone.",
    },
    noTracking: {
      title: "No Tracking",
      body: "WasOK does not use tracking pixels, analytics services, or any other technology that monitors your behavior or shares information with third parties. We don't track what products you scan, which stores you shop at, or how you use the app.",
    },
    sustainability: {
      title: "How We Stay Sustainable",
      body: "You might wonder how we keep WasOK free without ads or data selling. Our approach is simple:",
      items: [
        {
          label: "Free and Open:",
          text: "WasOK is free for everyone to use, with no premium tiers or paywalls",
        },
        {
          label: "Lean Operation:",
          text: "We keep our costs low by building efficient, privacy-focused software",
        },
        {
          label: "Optional Donations:",
          text: "Users who love WasOK can support us through voluntary donations",
        },
        {
          label: "Open Source:",
          text: "We may offer consulting or custom development services for organizations",
        },
      ],
    },
    noAffiliate: {
      title: "No Affiliate Links",
      body: "WasOK does not use affiliate links or receive commissions from retailers. When you scan a product, we don't redirect you to purchase it anywhere. The app is purely for your personal record-keeping and decision-making.",
    },
    noPromotions: {
      title: "No Product Promotions",
      body: "We do not accept payment from brands or manufacturers to promote their products. WasOK will never suggest products, highlight certain brands, or influence your ratings in any way. Your opinions are your own.",
    },
    partnerships: {
      title: "Third-Party Partnerships",
      body: "WasOK does not partner with marketing companies, data aggregators, or advertising networks. We operate independently and prioritize your privacy above all else.",
    },
    future: {
      title: "Future Plans",
      body: "We are committed to keeping WasOK ad-free and privacy-focused. If we ever need to introduce a sustainable business model in the future, we will:",
      items: [
        "Announce any changes well in advance",
        "Never compromise on privacy or data security",
        "Always offer a way to use the app without tracking or ads",
        "Listen to user feedback and concerns",
      ],
    },
    supporting: {
      title: "Supporting WasOK",
      body: "If you love WasOK and want to support our mission of privacy-focused shopping memory, you can:",
      items: [
        "Tell your friends and family about the app",
        "Leave a positive review on the Google Play Store",
        "Share your feedback and suggestions with us",
        "Consider making a voluntary donation (when available)",
      ],
    },
    cta: {
      heading: "Questions about our policies?",
      body: "We're transparent about how WasOK operates. If you have any questions about our approach to privacy, monetization, or sustainability, please reach out.",
      button: "Contact Us",
    },
  },
};
